import React, { useState, useEffect, useCallback, useRef } from 'react'
import styles from './styles.module.scss'
import logo from '../../../assets/sentinel-logo.png'

interface FloatingNavBarProps {
    className?: string
    // Not shown at all, e.g. once they've started the eligibility check
    hidden?: boolean
}

const FloatingNavBar: React.FC<FloatingNavBarProps> = ({ className, hidden = false }) => {
    const [isVisible, setIsVisible] = useState(false)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const currentTargetRef = useRef<Element | null>(null)

    const setupObserver = useCallback(() => {
        if (observerRef.current) {
            observerRef.current.disconnect()
        }

        const stepCard = document.querySelector('[class*="stepCard"]')
        if (!stepCard) return

        currentTargetRef.current = stepCard

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                // Show nav bar when the step card's bottom edge has scrolled out of the viewport
                setIsVisible(!entry.isIntersecting)
            },
            {
                threshold: 0,
                rootMargin: '0px 0px 0px 0px',
            },
        )

        observerRef.current.observe(stepCard)
    }, [])

    useEffect(() => {
        setupObserver()

        // Re-observe whenever the DOM changes (handles step card height changes)
        const mutationObserver = new MutationObserver(() => {
            const stepCard = document.querySelector('[class*="stepCard"]')
            if (stepCard && stepCard !== currentTargetRef.current) {
                setupObserver()
            }
        })

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        })

        return () => {
            observerRef.current?.disconnect()
            mutationObserver.disconnect()
        }
    }, [setupObserver])

    const scrollToForm = () => {
        // Scroll to the top of the page where the form starts
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }

    if (hidden) return null

    return (
        <nav className={`${styles.navBar} ${isVisible ? styles.visible : styles.hidden} ${className || ''}`}>
            <div className={styles.navContent}>
                <div className={styles.logo}>
                    <img src={logo} alt="Sentinel Legal" />
                </div>
                <button className={styles.submitButton} onClick={scrollToForm}>
                    Start a Claim
                </button>
            </div>
        </nav>
    )
}

export default FloatingNavBar
